import { action, makeObservable, observable, runInAction } from "mobx"

import type { ManualClGraphQlTransport } from "@/domains/thick-v1/manual-cl-graphql-transport"
import type { ManualClPoolActivityRow } from "@/domains/validators"

export type PoolActivityPageKind = "Swaps" | "Mints" | "Burns" | "Collects"

const KIND_TO_FIELD: Record<PoolActivityPageKind, "swaps" | "mints" | "burns" | "collects"> = {
  Swaps: "swaps",
  Mints: "mints",
  Burns: "burns",
  Collects: "collects",
}

/**
 * Observable, per-pool activity store. Owns paginated results for the pool
 * detail page's activity feed (swaps / mints / burns / collects) and a UI
 * filter state driving which GraphQL collection to render.
 *
 * Unlike the global `ManualClPositionOrchestrator` this store is loaded on
 * demand rather than polling — the pool detail page is a leaf view and
 * restarting an interval on navigation would hammer the indexer for no
 * reason. Lifecycle events from `manual-cl.*` can still force-refresh by
 * calling `reload()` from their handlers.
 */
export class PoolActivityStore {
  /** Activity rows currently loaded for the focused pool + tab. */
  rows: Array<ManualClPoolActivityRow> = []
  /** Currently-focused pool address (lower-cased). `undefined` = no page. */
  activePoolAddress: string | undefined = undefined
  /** Current filter tab. */
  activeKind: PoolActivityPageKind = "Swaps"
  /** Zero-indexed page offset × `pageSize`. */
  offset: number = 0
  /** Rows requested per fetch. Page-size change clears `rows` and refetches. */
  pageSize: number = 25
  /** Standard three-state loading flag. */
  isLoading: boolean = false
  error: string | undefined = undefined

  constructor(private readonly transport: ManualClGraphQlTransport) {
    makeObservable(this, {
      rows: observable,
      activePoolAddress: observable,
      activeKind: observable,
      offset: observable,
      pageSize: observable,
      isLoading: observable,
      error: observable,
      setActivePool: action,
      setActiveKind: action,
      nextPage: action,
      previousPage: action,
      reload: action,
    })
  }

  setActivePool(address: string | undefined): void {
    const next = address?.toLowerCase()
    if (next === this.activePoolAddress) return
    this.activePoolAddress = next
    this.offset = 0
    this.rows = []
    this.error = undefined
    if (next) {
      void this.fetch()
    }
  }

  setActiveKind(kind: PoolActivityPageKind): void {
    if (kind === this.activeKind) return
    this.activeKind = kind
    this.offset = 0
    this.rows = []
    this.error = undefined
    void this.fetch()
  }

  nextPage(): void {
    this.offset += this.pageSize
    void this.fetch()
  }

  previousPage(): void {
    this.offset = Math.max(0, this.offset - this.pageSize)
    void this.fetch()
  }

  reload(): void {
    void this.fetch()
  }

  private async fetch(): Promise<void> {
    const pool = this.activePoolAddress
    if (!pool) return
    const currentKind = this.activeKind
    const currentOffset = this.offset
    runInAction(() => {
      this.isLoading = true
      this.error = undefined
    })
    try {
      const response = await this.transport.loadPoolActivity(pool, this.pageSize, currentOffset)
      // Bail out if the user navigated away / changed filter while we were
      // waiting — using the snapshot guarantees stale responses don't
      // overwrite fresh ones.
      if (
        this.activePoolAddress !== pool ||
        this.activeKind !== currentKind ||
        this.offset !== currentOffset
      ) {
        return
      }
      const key = KIND_TO_FIELD[currentKind]
      runInAction(() => {
        this.rows = response[key] as Array<ManualClPoolActivityRow>
        this.isLoading = false
      })
    } catch (error) {
      runInAction(() => {
        this.isLoading = false
        this.error = error instanceof Error ? error.message : "Failed to load activity"
      })
    }
  }
}
